import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Car, Plus, LayoutDashboard, Route, Calendar, FileText, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';

function AdminVehicles() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/admin/vehicles');
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!vehicleNumber) {
      toast.error('Please enter vehicle number');
      return;
    }
    setCreating(true);
    try {
      await api.post('/admin/vehicles', { vehicle_number: vehicleNumber });
      toast.success('Vehicle created successfully');
      setVehicleNumber('');
      setOpen(false);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create vehicle');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex">
      <div className="w-64 bg-primary text-white p-6">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
            alt="Hasaka Tours & Travels"
            className="h-16 w-auto mb-3 brightness-0 invert"
          />
          <h2 className="font-manrope font-bold text-xl">Admin Panel</h2>
        </div>
        <nav className="space-y-2">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link
            to="/admin/vehicles"
            className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary-900 text-white font-medium"
          >
            <Car className="w-5 h-5" />
            Vehicles
          </Link>
          <Link
            to="/admin/routes"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
          >
            <Route className="w-5 h-5" />
            Routes
          </Link>
          <Link
            to="/admin/schedules"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
          >
            <Calendar className="w-5 h-5" />
            Schedules
          </Link>
          <Link
            to="/admin/bookings"
            className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium"
          >
            <FileText className="w-5 h-5" />
            Bookings
          </Link>
        </nav>
        <div className="mt-auto pt-12">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full mb-3 border-white text-white hover:bg-white hover:text-primary rounded-sm"
          >
            User Dashboard
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-white hover:bg-primary-900 rounded-sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex-1 p-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="vehicles-heading">
              Vehicles Management
            </h1>
            <p className="text-slate-600 font-public-sans text-lg">Manage your fleet of BYD eMax 7 vehicles</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-6 py-3" data-testid="add-vehicle-button">
                <Plus className="w-5 h-5 mr-2" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl text-primary">Add New Vehicle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 uppercase tracking-wide">
                    Vehicle Number
                  </label>
                  <Input
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value)}
                    className="rounded-none border-slate-300 h-12"
                    placeholder="e.g., AP-09-XY-1234"
                    data-testid="vehicle-number-input"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3 w-full"
                  data-testid="create-vehicle-button"
                >
                  {creating ? 'Creating...' : 'Create Vehicle'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading vehicles...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.vehicle_id}
                className="bg-white rounded-sm p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`vehicle-${vehicle.vehicle_id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-sm flex items-center justify-center">
                    <Car className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="font-mono font-bold text-primary text-lg">{vehicle.vehicle_number}</p>
                    <p className="text-sm text-slate-500 font-public-sans">{vehicle.model}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 font-public-sans">Capacity:</span>
                    <span className="font-semibold text-primary">{vehicle.capacity} seats</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 font-public-sans">Status:</span>
                    <span
                      className={`px-2 py-1 rounded-sm text-xs font-semibold uppercase ${
                        vehicle.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {vehicle.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminVehicles;